// @file Store to handle drag and drop events globally
import { usePostComposerModalStore } from '@@/pinia/post_composer_modal_store'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export enum DropZone {
  DOCUMENT = 'document',
  POST_COMPOSER = 'post_composer',
  EXPANDED_VIEW = 'expanded_view',
  POST_COMMENT_SECTION = 'post_comment_section',
}

export const useSurfaceDragAndDropEventsStore = defineStore('surfaceDragAndDropEvents', () => {
  /* ---------------------- */
  /* STATE                  */
  /* ---------------------- */

  const isHandlingDragEvent = ref<boolean>(false)
  const activeDropZone = ref<DropZone | null>(null)
  const dropActionMessage = ref<string | null>(null)
  const dropActionAllowed = ref<boolean>(true)

  /* ---------------------- */
  /* COMPUTED               */
  /* ---------------------- */
  const postComposerModalStore = usePostComposerModalStore()
  const xDropActionMessageToCreatePostOutsideComposer = computed(
    () => activeDropZone.value === DropZone.DOCUMENT && postComposerModalStore.hasActiveComposerModal,
  )
  const xDropActionMessageWithoutActiveDraft = computed(
    () => activeDropZone.value === DropZone.DOCUMENT && !postComposerModalStore.hasActiveComposerModal,
  )

  const xDropZoneToCreatePost = computed(
    () =>
      isHandlingDragEvent.value &&
      xDropActionMessageToCreatePostOutsideComposer.value &&
      !postComposerModalStore.xEditThumbnailModal,
  )
  const xDropZoneWithoutActiveDraft = computed(
    () => isHandlingDragEvent.value && xDropActionMessageWithoutActiveDraft.value,
  )

  /* ---------------------- */
  /* ACTIONS                */
  /* ---------------------- */

  const startHandlingDragEvent = (): void => {
    isHandlingDragEvent.value = true
  }

  const stopHandlingDragEvent = (): void => {
    isHandlingDragEvent.value = false
  }

  const setActiveDropZone = (dropZone: DropZone | null): void => {
    activeDropZone.value = dropZone
  }

  const setDropActionMessage = (newDropActionMessage: string | null): void => {
    dropActionMessage.value = newDropActionMessage
  }

  const setDropActionAllowed = (allowed: boolean): void => {
    dropActionAllowed.value = allowed
  }

  return {
    // Getters
    isHandlingDragEvent,
    activeDropZone,
    dropActionMessage,
    dropActionAllowed,
    xDropZoneToCreatePost,
    xDropZoneWithoutActiveDraft,
    xDropActionMessageToCreatePostOutsideComposer,
    xDropActionMessageWithoutActiveDraft,

    // Actions
    startHandlingDragEvent,
    stopHandlingDragEvent,
    setActiveDropZone,
    setDropActionMessage,
    setDropActionAllowed,
  }
})
